import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { useCartStore } from "../../store/cartStore";
import { CURRENCY_MAP, serviceKeys } from "../../constants";
import countryList from "react-select-country-list";
import Select from "react-select";
import {
  createPublicOrder,
  validateVoucher,
} from "../../api/ticketsPlatform/checkout";
import {
  requestOtpForMail,
  verifyOtpForMail,
} from "../../api/ticketsPlatform/auth";
import { useUserStore } from "../../store/userStore";
import Navbar from "../../components/tickets/navBar";
import Footer from "../../components/tickets/footer";
import { usePixel } from "../../hooks/usePixel";
import { allCountries } from "country-telephone-data";
import { useTranslation } from "react-i18next";

const getRemaining = (ticket) => {
  if (ticket.quantity_total != null && ticket.quantity_sold != null) {
    return Math.max(0, ticket.quantity_total - ticket.quantity_sold);
  }
  return 20;
};

const countries = countryList().getData();

const getFlagEmoji = (countryCode) =>
  countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));

const phoneCountryOptions = allCountries.map((country) => ({
  label: `${getFlagEmoji(country.iso2)} +${country.dialCode}`,
  value: `+${country.dialCode}`,
}));

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#031c14",
    borderColor: state.isFocused ? "rgba(34,197,94,0.6)" : "rgba(20,83,45,0.4)",
    borderRadius: "0.75rem",
    padding: "2px 4px",
    boxShadow: "none",
    "&:hover": { borderColor: "rgba(34,197,94,0.4)" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#031c14",
    border: "1px solid rgba(20,83,45,0.4)",
    borderRadius: "0.75rem",
    overflow: "hidden",
  }),
  menuList: (base) => ({ ...base, padding: 0 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#042018" : "transparent",
    color: "#fff",
    fontSize: "0.875rem",
    cursor: "pointer",
  }),
  singleValue: (base) => ({ ...base, color: "#fff", fontSize: "0.875rem" }),
  input: (base) => ({ ...base, color: "#fff", fontSize: "0.875rem" }),
  placeholder: (base) => ({ ...base, color: "#4b5563", fontSize: "0.875rem" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "rgba(34,197,94,0.4)" }),
};

export default function Checkout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    items,
    removeItem,
    updateQuantity,
    getSubtotal,
    getCommission,
    getVat,
    getTotal,
    tables: cartTables,
    removeTable,
  } = useCartStore();

  const subtotal = getSubtotal();
  const commission = getCommission();
  const total = getTotal();
  const vat = getVat();
  const hasCommission = commission > 0;
  const hasVat = vat > 0;
  const currency = items[0]?.currency || cartTables[0]?.currency;

  const { trackEvent } = usePixel(items[0]?.pixels || []);

  // OTP STATE
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpSuccess, setOtpSuccess] = useState(null);
  const [resendCountdown, setResendCountdown] = useState(0);

  const [otpVerificationToken, setOtpVerificationToken] = useState(null);

  const [showVoucher, setShowVoucher] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState(null);
  const [voucherData, setVoucherData] = useState(null);

  const { isAuthenticated, token, user } = useUserStore();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    gender: "",
    address: "",
    city: "",
    postcode: "",
    country: null,
    phoneCode: "+34",
    phone: "",
    voucherCode: "",
    affiliateCode: "",
    createAccount: true,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      setForm((f) => ({
        ...f,
        fullName: `${user.first_name} ${user.last_name}` || "",
        email: user.email || "",
        gender: user.gender || "",
        phoneCode: user.phone_country || "+34",
        phone: user.phone_number || "",
        address: user.billing_address?.address || "",
        city: user.billing_address?.city || "",
        postcode: user.billing_address?.postcode || "",
        country: user.billing_address
          ? {
              label: user.billing_address.country,
              value: user.billing_address.country_code,
            }
          : null,
      }));
    }
  }, [isAuthenticated, user]);

  const formatCurrency = (amount, cur) => {
    const symbol = CURRENCY_MAP[cur || currency] || cur || currency;
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = t("checkout.required");
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      e.email = t("checkout.emailInvalid");
    if (!isAuthenticated && !otpVerified)
      e.email = t("checkout.emailVerifyRequired");
    if (!form.address.trim()) e.address = t("checkout.required");
    if (!form.city.trim()) e.city = t("checkout.required");
    if (!form.postcode.trim()) e.postcode = t("checkout.required");
    if (!form.country) e.country = t("checkout.required");
    return e;
  };

  const handleVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherLoading(true);
    setVoucherError(null);
    setVoucherData(null);
    try {
      const eventId = items[0]?.eventId || cartTables[0]?.eventId;
      const data = await validateVoucher(
        voucherCode.trim(),
        eventId,
        isAuthenticated ? token : null,
      );
      if (data.valid) {
        setVoucherData(data);
      } else {
        setVoucherError(data.detail || t("checkout.voucherInvalid"));
      }
    } catch (err) {
      setVoucherError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t("checkout.voucherInvalid"),
      );
    } finally {
      setVoucherLoading(false);
    }
  };

  const getDiscount = () => {
    if (!voucherData) return 0;
    if (voucherData.type === "percentage") {
      return (subtotal * parseFloat(voucherData.value)) / 100;
    }
    return parseFloat(voucherData.value || 0);
  };

  const discountAmount = getDiscount();
  const finalTotal = Math.max(0, total - discountAmount);

  const handleChange = (field, value) => {
    if (field === "fullName") {
      value = value.replace(/[@!$]/g, "");
    }
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
    if (apiError) setApiError(null);
    if (field === "email") {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpValues(["", "", "", "", "", ""]);
      setOtpError(null);
      setOtpSuccess(null);
      setResendCountdown(0);
      setOtpVerificationToken(null);
    }
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    const payload = {
      event: items[0]?.eventId || cartTables[0]?.eventId,
      email: form.email,
      otp_verification_token: otpVerificationToken || "",
      full_name: form.fullName,
      gender: form.gender,
      phone_country: form.phoneCode,
      phone_number: form.phone,
      ...(voucherData && { voucher_code: voucherCode.trim() }),
      billing_address: {
        address: form.address,
        city: form.city,
        postcode: form.postcode,
        country: form.country.label,
        country_code: form.country.value,
      },
      items: items.map((i) => ({
        ticket_type_id: i.ticket.id,
        quantity: i.quantity,
      })),
      create_account: form.createAccount,
      ...(cartTables.length > 0 && {
        tables: cartTables.map((t) => ({
          table_id: t.id,
          // custom_fields_values: {
          //   address: form.address, // ⚠️ confirm key with backend
          //   phone: `${form.phoneCode}${form.phone}`, // ⚠️ confirm key with backend
          // },
        })),
      }),
      // ...(form.voucherCode.trim() && { voucher_code: form.voucherCode.trim() }),
      // ...(form.affiliateCode.trim() && { affiliate_code: form.affiliateCode.trim() }),
    };

    try {
      setSubmitting(true);
      setApiError(null);
      const order = await createPublicOrder(
        payload,
        isAuthenticated ? token : null,
      );

      trackEvent("SubmitCheckoutForm", {
        value: total,
        currency: currency,
      });

      navigate("/tickets-store/payment", {
        state: {
          orderNumber: order.order_number,
          total: order.total,
          currency: order.currency,
          expiresAt:
            order.expires_in_minutes != null
              ? Date.now() + order.expires_in_minutes * 60 * 1000
              : Date.now() + 5 * 60 * 1000,
          clientSecret: order.client_secret,
          stripePublicKey: order.stripe_public_key,
          buyerEmail: form.email,
          pixels: items[0]?.pixels || [],
        },
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        t("checkout.genericError");
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const sendOtp = async (email) => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      await requestOtpForMail(email, "checkout");
      setOtpSent(true);
      setOtpSuccess(t("checkout.otpSentTo") + email);
      startResendCountdown();
    } catch (err) {
      setOtpError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t("checkout.otpErrorSend"),
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    const code = otpValues.join("");
    if (code.length !== 6) return;
    setOtpLoading(true);
    setOtpError(null);
    try {
      const data = await verifyOtpForMail(form.email, code, "checkout");
      setOtpVerified(true);
      setOtpVerificationToken(data.otp_verification_token); // store token
      setOtpSuccess(t("checkout.otpSuccess"));
    } catch (err) {
      setOtpError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t("checkout.otpErrorVerify"),
      );
      setOtpValues(["", "", "", "", "", ""]);
    } finally {
      setOtpLoading(false);
    }
  };

  const startResendCountdown = () => {
    setResendCountdown(60);
    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // numbers only
    const newValues = [...otpValues];
    newValues[index] = value.slice(-1);
    setOtpValues(newValues);
    setOtpError(null);
    // auto focus next
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newValues = [...otpValues];
    pasted.split("").forEach((char, i) => {
      newValues[i] = char;
    });
    setOtpValues(newValues);
    document.getElementById(`otp-${Math.min(pasted.length, 5)}`)?.focus();
  };

  /* ── EMPTY STATE ── */
  if (items.length === 0 && (cartTables || []).length === 0) {
    return (
      <div className="bg-[#021610] text-white min-h-screen flex flex-col items-center justify-center gap-4">
        <span className="text-5xl">🎟</span>
        <p className="text-lg font-semibold">{t("checkout.emptyCart")}</p>
        <button
          onClick={() => navigate("/tickets-store/")}
          className="mt-2 px-6 py-2 rounded-xl border border-green-500 text-green-400 text-sm hover:bg-green-500 hover:text-black transition"
        >
          {t("checkout.viewEvents")}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#021610] text-white min-h-screen">
      {/* NAVBAR */}
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
        {/* BACK */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition mb-8"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t("checkout.back")}
        </button>

        {/* STEPPER */}
        <div className="flex items-center w-full text-xs text-green-400/70 mb-10">
          <StepDot number="1" label={t("checkout.stepTicketType")} done />
          <StepLine />
          <StepDot number="2" label={t("checkout.stepConfirm")} active />
          <StepLine />
          <StepDot number="3" label={t("checkout.stepPayment")} />
        </div>

        <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
          {t("checkout.step2")}
        </p>
        <h1 className="text-3xl md:text-4xl font-bold mt-1 mb-10">
          {t("checkout.confirmOrder")}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* ── LEFT — FORM ── */}
          <div className="lg:col-span-3 space-y-6">
            <div className="space-y-4">
              <SectionLabel label={t("checkout.contactInfo")} />
              <Field
                label={t("checkout.fullName")}
                value={form.fullName}
                onChange={(v) => handleChange("fullName", v)}
                error={errors.fullName}
                placeholder={t("checkout.fullNamePlaceholder")}
              />
              {/* <Field
                label="Email"
                type="email"
                value={form.email}
                disabled={isAuthenticated}
                onChange={(v) => handleChange("email", v)}
                error={errors.email}
                placeholder="tu@email.com"
              /> */}

              {/* EMAIL + OTP */}
              {!isAuthenticated ? (
                <div className="space-y-3">
                  {/* EMAIL ROW */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                      Email
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={form.email}
                        disabled={otpVerified}
                        onChange={(e) =>
                          handleChange("email", e.target.value.toLowerCase())
                        }
                        placeholder="tu@email.com"
                        className={`flex-1 bg-[#031c14] border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition
            ${
              errors.email
                ? "border-red-500/60"
                : otpVerified
                  ? "border-green-500/60 opacity-70"
                  : "border-green-900/40 focus:border-green-500/60"
            }`}
                      />
                      {!otpVerified && (
                        <button
                          onClick={() => {
                            if (
                              !form.email ||
                              !/\S+@\S+\.\S+/.test(form.email)
                            ) {
                              setErrors((e) => ({
                                ...e,
                                email: t("checkout.emailInvalid"),
                              }));
                              return;
                            }
                            sendOtp(form.email);
                          }}
                          disabled={otpLoading || resendCountdown > 0}
                          className={`flex-shrink-0 px-4 py-3 rounded-xl text-xs font-bold tracking-widest uppercase transition
              ${
                otpLoading || resendCountdown > 0
                  ? "bg-green-900/30 text-green-900 cursor-not-allowed"
                  : "bg-green-500 text-black hover:bg-green-400"
              }`}
                        >
                          {otpLoading
                            ? "..."
                            : otpSent
                              ? resendCountdown > 0
                                ? `${resendCountdown}s`
                                : t("checkout.resend")
                              : t("checkout.verify")}
                        </button>
                      )}
                      {otpVerified && (
                        <div className="flex-shrink-0 flex items-center gap-2">
                          <div className="flex items-center gap-1.5 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/20">
                            <span className="text-green-400 text-sm">✓</span>
                            <span className="text-green-400 text-xs tracking-widest uppercase">
                              {t("checkout.verified")}
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              setOtpSent(false);
                              setOtpVerified(false);
                              setOtpValues(["", "", "", "", "", ""]);
                              setOtpError(null);
                              setOtpSuccess(null);
                            }}
                            className="px-3 py-3 rounded-xl border border-green-900/40 text-gray-400 text-xs tracking-widest uppercase hover:border-green-700/50 hover:text-white transition"
                          >
                            {t("checkout.change")}
                          </button>
                        </div>
                      )}
                    </div>
                    {errors.email && (
                      <p className="text-[10px] text-red-400 tracking-wide">
                        {errors.email}
                      </p>
                    )}
                    {otpError && !otpSent && (
                      <p className="text-[10px] text-red-400 tracking-wide">
                        {otpError}
                      </p>
                    )}
                  </div>

                  {/* OTP BOXES */}
                  {otpSent && !otpVerified && (
                    <div className="bg-[#031c14] border border-green-900/30 rounded-xl p-4 space-y-4">
                      <div>
                        <p className="text-sm text-white font-medium mb-1">
                          {t("checkout.otpTitle")}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("checkout.otpSubtitle")}{" "}
                          <span className="text-green-400">{form.email}</span>
                        </p>
                      </div>

                      {/* 6 DIGIT BOXES */}
                      <div className="flex gap-2 justify-between">
                        {otpValues.map((val, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={val}
                            onChange={(e) =>
                              handleOtpChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            onPaste={index === 0 ? handleOtpPaste : undefined}
                            className={`w-full aspect-square max-w-[48px] text-center text-lg font-bold bg-[#021610] border rounded-xl text-white outline-none transition
                ${
                  otpError
                    ? "border-red-500/60"
                    : val
                      ? "border-green-500/60 text-green-400"
                      : "border-green-900/40 focus:border-green-500/60"
                }`}
                          />
                        ))}
                      </div>

                      {/* ERROR / SUCCESS */}
                      {otpError && (
                        <p className="text-[10px] text-red-400 tracking-wide">
                          {otpError}
                        </p>
                      )}

                      {/* VERIFY BUTTON */}
                      <button
                        onClick={verifyOtp}
                        disabled={otpValues.join("").length !== 6 || otpLoading}
                        className={`w-full py-3 rounded-xl font-bold tracking-widest uppercase text-sm transition
            ${
              otpValues.join("").length !== 6 || otpLoading
                ? "bg-green-900/30 text-green-900 cursor-not-allowed"
                : "bg-green-500 text-black hover:bg-green-400"
            }`}
                      >
                        {otpLoading
                          ? t("checkout.verifying")
                          : t("checkout.confirmCode")}
                      </button>
                    </div>
                  )}

                  {/* SUCCESS MESSAGE */}
                  {otpSuccess && otpVerified && (
                    <p className="text-[10px] text-green-400 tracking-wide">
                      ✓ {otpSuccess}
                    </p>
                  )}
                </div>
              ) : (
                /* LOGGED IN — show email field disabled, no OTP needed */
                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  disabled={true}
                  onChange={(v) => handleChange("email", v)}
                  error={errors.email}
                  placeholder="tu@email.com"
                />
              )}

              {/* GENDER */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                  {t("checkout.gender")}
                </label>
                <div className="relative">
                  <select
                    value={form.gender}
                    onChange={(e) => handleChange("gender", e.target.value)}
                    className="w-full bg-[#031c14] border border-green-900/40 rounded-xl px-4 py-3 text-sm text-white outline-none transition focus:border-green-500/60 appearance-none cursor-pointer pr-10"
                  >
                    <option value="" disabled>
                      {t("checkout.genderSelect")}
                    </option>
                    <option value="male">{t("checkout.genderMale")}</option>
                    <option value="female">{t("checkout.genderFemale")}</option>
                    <option value="other">{t("checkout.genderOther")}</option>
                  </select>
                  {/* CHEVRON ICON */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-green-400/50">
                    <ChevronDownIcon className="w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* PHONE */}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                  {t("checkout.phone")}
                </label>
                <div className="flex items-center gap-2">
                  {/* COUNTRY CODE */}
                  <div className="w-32 flex-shrink-0">
                    <Select
                      options={phoneCountryOptions}
                      value={phoneCountryOptions.find(
                        (o) => o.value === form.phoneCode,
                      )}
                      onChange={(v) => handleChange("phoneCode", v.value)}
                      styles={{
                        ...selectStyles,
                        control: (base, state) => ({
                          ...selectStyles.control(base, state),
                          padding: "1px 2px",
                        }),
                      }}
                      menuPlacement="auto"
                    />
                  </div>
                  {/* NUMBER */}
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={(e) =>
                      handleChange("phone", e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="600 000 000"
                    className="flex-1 bg-[#031c14] border border-green-900/40 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition focus:border-green-500/60"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500 leading-relaxed">
                {t("checkout.emailHint")}
              </p>

              {/* CREATE ACCOUNT CHECKBOX */}
              {!isAuthenticated && (
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={form.createAccount}
                      onChange={(e) =>
                        handleChange("createAccount", e.target.checked)
                      }
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-md border transition flex items-center justify-center
        ${
          form.createAccount
            ? "bg-green-500 border-green-500"
            : "bg-[#031c14] border-green-900/40 group-hover:border-green-500/50"
        }`}
                    >
                      {form.createAccount && (
                        <span className="text-black text-xs font-bold leading-none">
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-white">
                      {t("checkout.createAccount")}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t("checkout.createAccountHint")}
                    </p>
                  </div>
                </label>
              )}
            </div>

            <div className="space-y-4">
              <SectionLabel label={t("checkout.billingAddress")} />
              <Field
                label={t("checkout.address")}
                value={form.address}
                onChange={(v) => handleChange("address", v)}
                error={errors.address}
                placeholder={t("checkout.addressPlaceholder")}
              />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label={t("checkout.city")}
                  value={form.city}
                  onChange={(v) => handleChange("city", v)}
                  error={errors.city}
                  placeholder={t("checkout.cityPlaceholder")}
                />
                <Field
                  label={t("checkout.postcode")}
                  value={form.postcode}
                  onChange={(v) => handleChange("postcode", v)}
                  error={errors.postcode}
                  placeholder={t("checkout.postcodePlaceholder")}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                  {t("checkout.country")}
                </label>
                <Select
                  options={countries}
                  value={form.country}
                  onChange={(v) => handleChange("country", v)}
                  placeholder={t("checkout.countryPlaceholder")}
                  styles={selectStyles}
                  menuPlacement="auto"
                />
                {errors.country && (
                  <p className="text-[10px] text-red-400 tracking-wide">
                    {errors.country}
                  </p>
                )}
              </div>
            </div>

            {/* <div className="space-y-4">
              <SectionLabel label="Códigos opcionales" />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Código de descuento"
                  value={form.voucherCode}
                  onChange={(v) => handleChange("voucherCode", v)}
                  placeholder="DESCUENTO10"
                />
                <Field
                  label="Código de afiliado"
                  value={form.affiliateCode}
                  onChange={(v) => handleChange("affiliateCode", v)}
                  placeholder="AFFY123"
                />
              </div>
            </div> */}

            {apiError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                <p className="text-sm text-red-400">{apiError}</p>
              </div>
            )}
          </div>

          {/* ── RIGHT — ORDER SUMMARY ── */}
          <div className="lg:col-span-2">
            <SectionLabel label={t("checkout.orderSummary")} />

            <div className="space-y-3 mt-4">
              {items.map((item) => (
                <OrderItem
                  key={`${item.eventId}-${item.ticket.id}`}
                  item={item}
                  onRemove={() => removeItem(item.ticket.id, item.eventId)}
                  onQtyChange={(q) =>
                    updateQuantity(item.ticket.id, item.eventId, q)
                  }
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>

            {/* TABLE ITEMS */}
            {cartTables.map((table) => (
              <div
                key={`table-${table.id}`}
                className="bg-[#031c14] border border-green-900/30 rounded-xl p-4 mt-2"
              >
                <p className="text-[10px] tracking-widest text-green-400/60 uppercase mb-1">
                  {t("checkout.table")}
                </p>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold">
                    {t("checkout.tableItem", { name: table.name })}
                  </p>
                  <button
                    onClick={() => removeTable(table.id)}
                    className="text-gray-600 hover:text-red-400 transition flex-shrink-0 mt-0.5"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-gray-500">
                    {t("checkout.tableQty")}
                  </p>
                  <p className="text-sm font-semibold text-white">
                    {formatCurrency(
                      parseFloat(table.display_price || 0),
                      currency,
                    )}
                  </p>
                </div>
              </div>
            ))}

            {/* PRICE BREAKDOWN */}
            <div className="mt-5 space-y-2">
              {/* SUBTOTAL */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                  {t("checkout.subtotal")}
                </p>
                <p className="text-sm text-white">
                  {formatCurrency(subtotal, currency)}
                </p>
              </div>

              {/* COMMISSION */}
              {hasCommission && (
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                    {t("checkout.commission")}
                  </p>
                  <p className="text-sm text-gray-300">
                    {formatCurrency(commission, currency)}
                  </p>
                </div>
              )}

              {hasVat && (
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                    {t("checkout.vat")}
                  </p>
                  <p className="text-sm text-gray-300">
                    {formatCurrency(vat, currency)}
                  </p>
                </div>
              )}

              {/* VOUCHER INPUT */}
              {/* VOUCHER INPUT */}
              <div className="border-t border-green-900/40 pt-3">
                {!voucherData && !showVoucher && (
                  <button
                    onClick={() => setShowVoucher(true)}
                    className="text-[10px] tracking-widest text-green-400/60 hover:text-green-400 uppercase transition"
                  >
                    {t("checkout.haveVoucher")}
                  </button>
                )}
                {!voucherData && showVoucher && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={voucherCode}
                      onChange={(e) => {
                        setVoucherCode(e.target.value.toUpperCase());
                        setVoucherError(null);
                      }}
                      placeholder={t("checkout.voucherPlaceholder")}
                      className="flex-1 bg-[#031c14] border border-green-900/40 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 outline-none transition focus:border-green-500/60"
                    />
                    <button
                      onClick={handleVoucher}
                      disabled={voucherLoading || !voucherCode.trim()}
                      className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-bold tracking-widest uppercase transition
          ${
            voucherLoading || !voucherCode.trim()
              ? "bg-green-900/30 text-green-900 cursor-not-allowed"
              : "bg-green-500 text-black hover:bg-green-400"
          }`}
                    >
                      {voucherLoading
                        ? t("checkout.voucherApplying")
                        : t("checkout.voucherApply")}
                    </button>
                  </div>
                )}
                {voucherData && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] tracking-widest text-green-400 uppercase">
                        {voucherCode}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {voucherData.type === "percentage"
                          ? `${parseFloat(voucherData.value).toFixed(0)}% ${t("checkout.voucherApplied")}`
                          : `${formatCurrency(parseFloat(voucherData.value), currency)} ${t("checkout.voucherApplied")}`}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setVoucherData(null);
                        setVoucherCode("");
                        setVoucherError(null);
                        setShowVoucher(false);
                      }}
                      className="text-[10px] text-red-400 hover:text-red-300 tracking-widest uppercase transition"
                    >
                      {t("checkout.voucherRemove")}
                    </button>
                  </div>
                )}
                {voucherError && (
                  <p className="text-[10px] text-red-400 tracking-wide mt-1">
                    {voucherError}
                  </p>
                )}
              </div>

              {/* DISCOUNT LINE */}
              {voucherData && discountAmount > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-[10px] tracking-widest text-green-400 uppercase">
                    {t("checkout.voucherDiscount")}
                  </p>
                  <p className="text-sm text-green-400">
                    -{formatCurrency(discountAmount, currency)}
                  </p>
                </div>
              )}

              {/* TOTAL — update to show finalTotal */}
              <div className="border-t border-green-900/40 pt-3 flex items-center justify-between">
                <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                  {t("checkout.total")}
                </p>
                <p className="text-2xl font-bold text-green-400">
                  {formatCurrency(finalTotal, currency)}
                </p>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-full mt-6 py-4 rounded-xl font-bold tracking-widest uppercase text-sm transition
                ${
                  submitting
                    ? "bg-green-700 text-black/50 cursor-not-allowed"
                    : "bg-green-500 text-black hover:bg-green-400"
                }`}
            >
              {submitting
                ? t("checkout.processing")
                : t("checkout.goToPayment")}
            </button>

            <p className="text-center text-[10px] text-gray-600 mt-4 tracking-wider uppercase">
              {t("checkout.securePayment")}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ── HELPERS ── */

function SectionLabel({ label }) {
  return (
    <p className="text-[10px] tracking-widest text-green-400/80 uppercase border-b border-green-900/30 pb-2">
      {label}
    </p>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  placeholder,
  type = "text",
  disabled = false,
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] tracking-widest text-gray-400 uppercase">
        {label}
      </label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`bg-[#031c14] border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition
          ${
            error
              ? "border-red-500/60 focus:border-red-500"
              : "border-green-900/40 focus:border-green-500/60"
          }`}
      />
      {error && (
        <p className="text-[10px] text-red-400 tracking-wide">{error}</p>
      )}
    </div>
  );
}

function OrderItem({ item, onRemove, onQtyChange, formatCurrency }) {
  const { t } = useTranslation();
  const remaining = getRemaining(item.ticket);
  const atMax = item.quantity >= remaining;
  const atMin = item.quantity <= 1;

  return (
    <div className="bg-[#031c14] border border-green-900/30 rounded-xl p-4">
      <p className="text-[10px] tracking-widest text-green-400/60 uppercase mb-1">
        {item.eventName}
      </p>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold">{item.ticket.name}</p>
        <button
          onClick={onRemove}
          className="text-gray-600 hover:text-red-400 transition flex-shrink-0 mt-0.5"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
      {item.ticket.tickets_per_order > 1 && (
        <p className="text-[10px] text-gray-500 mt-0.5">
          {t("checkout.ticketsPerUnit", {
            count: item.ticket.tickets_per_order,
          })}
        </p>
      )}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1.5 bg-[#021610] rounded-full px-1 py-1 border border-green-900/40">
          <button
            onClick={() => onQtyChange(item.quantity - 1)}
            disabled={atMin}
            className={`w-6 h-6 rounded-full transition flex items-center justify-center text-sm leading-none
              ${atMin ? "text-green-900 cursor-not-allowed" : "text-green-400 hover:bg-green-900/50"}`}
          >
            −
          </button>
          <span className="text-sm w-5 text-center tabular-nums">
            {item.quantity}
          </span>
          <button
            onClick={() => onQtyChange(item.quantity + 1)}
            disabled={atMax}
            className={`w-6 h-6 rounded-full transition flex items-center justify-center text-sm leading-none
              ${atMax ? "text-green-900 cursor-not-allowed" : "text-green-400 hover:bg-green-900/50"}`}
          >
            +
          </button>
        </div>
        <p className="text-sm font-semibold text-white">
          {formatCurrency(item.ticket.price * item.quantity, item.currency)}
        </p>
      </div>
      {atMax && (
        <p className="text-[10px] text-yellow-500/70 mt-2 tracking-wide">
          {t("checkout.maxReached")}
        </p>
      )}
    </div>
  );
}

function StepLine() {
  return <div className="flex-1 h-px bg-green-900/30 mx-3" />;
}

function StepDot({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold
          ${done ? "bg-green-700 text-white" : ""}
          ${active ? "bg-green-500 text-black" : ""}
          ${!done && !active ? "border border-green-500/30 text-green-400" : ""}
        `}
      >
        {done ? "✓" : number}
      </div>
      <span className="hidden md:block whitespace-nowrap text-[10px] tracking-widest">
        {label}
      </span>
    </div>
  );
}
